import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Calendar, MapPin, Users, Plus, Search, Filter, Edit, Trash2, 
  QrCode, Upload, Download, X, Check, Image as ImageIcon, Ticket
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PWAInstallPrompt from '../components/PWAInstallPrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventbriteClone() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('browse'); // browse, my-events, create
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  
  // Create event form
  const [newEvent, setNewEvent] = useState({
    name: '',
    date: '',
    time: '',
    endTime: '',
    location: '',
    description: '',
    category: '',
    capacity: '',
    isPublic: true,
    ticketPrice: 0,
    requiresRegistration: true,
    coverImage: null
  });
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadCategories();
    if (activeTab === 'browse') {
      loadPublicEvents();
    } else if (activeTab === 'my-events') {
      loadMyEvents();
    }
  }, [activeTab, selectedCategory, searchTerm]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/events/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadPublicEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/events/public?${params.toString()}`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to load events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const loadMyEvents = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/events/my-events`, { headers });
      setMyEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to load my events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async () => {
    if (!newEvent.name || !newEvent.date || !newEvent.time || !newEvent.location || !newEvent.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/events`,
        {
          ...newEvent,
          capacity: newEvent.capacity ? parseInt(newEvent.capacity) : null,
          ticketPrice: parseFloat(newEvent.ticketPrice || 0)
        },
        { headers }
      );
      
      const eventId = response.data.event.id;
      
      // Upload cover image if selected
      if (coverImageFile) {
        const formData = new FormData();
        formData.append('file', coverImageFile);
        await axios.post(`${API}/events/${eventId}/cover-image`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success('Event created successfully!');
      setNewEvent({
        name: '',
        date: '',
        time: '',
        endTime: '',
        location: '',
        description: '',
        category: '',
        capacity: '',
        isPublic: true,
        ticketPrice: 0,
        requiresRegistration: true,
        coverImage: null
      });
      setCoverImageFile(null);
      setCoverImagePreview(null);
      setActiveTab('browse');
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('Failed to create event');
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const registerForEvent = async (eventId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/events/${eventId}/register`,
        {},
        { headers }
      );
      toast.success('Registered successfully!');
      loadPublicEvents();
    } catch (error) {
      console.error('Failed to register:', error);
      toast.error(error.response?.data?.detail || 'Failed to register');
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/events/${eventId}`, { headers });
      toast.success('Event deleted!');
      loadMyEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    }
  };

  const viewEventDetails = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Events</h1>
          <p className="text-gray-600">Create, discover, and share amazing events</p>
        </div>

        {/* PWA Install Prompt */}
        <PWAInstallPrompt
          appName="Digi Events"
          appIcon="🎉"
          appColor="#8b5cf6"
          storageKey="digievents-install-prompt"
        />

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('browse')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'browse'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Browse Events
          </button>
          <button
            onClick={() => setActiveTab('my-events')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'my-events'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            My Events
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'create'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Plus className="w-4 h-4 inline mr-2" />
            Create Event
          </button>
          <button
            onClick={() => navigate('/photo-video-events')}
            className="px-6 py-3 font-medium text-pink-600 hover:text-pink-700 transition-colors flex items-center gap-2 ml-auto"
          >
            <ImageIcon className="w-4 h-4" />
            📸 Photo & Video Share
          </button>
        </div>

        {/* Browse Events Tab */}
        {activeTab === 'browse' && (
          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Events Grid */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading events...</p>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No events found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((event) => (
                  <Card key={event.id} className="hover:shadow-lg transition-shadow cursor-pointer overflow-hidden">
                    {event.coverImage && (
                      <div className="w-full h-48 overflow-hidden">
                        <img 
                          src={`${API}/${event.coverImage}`} 
                          alt={event.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg mb-2">{event.name}</CardTitle>
                          <span className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                            {event.category}
                          </span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-2" />
                          {new Date(event.date).toLocaleDateString()} at {event.time}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mr-2" />
                          {event.location}
                        </div>
                        {event.capacity && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="w-4 h-4 mr-2" />
                            {event.attendeeCount || 0} / {event.capacity} attendees
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{event.description}</p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => viewEventDetails(event.id)}
                          className="flex-1 bg-purple-600 hover:bg-purple-700"
                        >
                          View Details
                        </Button>
                        {event.requiresRegistration && (
                          <Button
                            onClick={() => registerForEvent(event.id)}
                            variant="outline"
                            className="flex-1"
                          >
                            <Ticket className="w-4 h-4 mr-2" />
                            Register
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* My Events Tab */}
        {activeTab === 'my-events' && (
          <div className="space-y-6">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading events...</p>
              </div>
            ) : myEvents.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">You haven't created any events yet</p>
                <Button onClick={() => setActiveTab('create')} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Event
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {myEvents.map((event) => (
                  <Card key={event.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{event.name}</h3>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                            <span className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {new Date(event.date).toLocaleDateString()} at {event.time}
                            </span>
                            <span className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {event.location}
                            </span>
                            <span className="flex items-center">
                              <Users className="w-4 h-4 mr-1" />
                              {event.attendeeCount || 0} attendees
                            </span>
                          </div>
                          <p className="text-gray-600">{event.description}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            onClick={() => viewEventDetails(event.id)}
                            variant="outline"
                            size="sm"
                          >
                            <QrCode className="w-4 h-4 mr-1" />
                            QR & Photos
                          </Button>
                          <Button
                            onClick={() => deleteEvent(event.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Event Tab */}
        {activeTab === 'create' && (
          <Card>
            <CardHeader>
              <CardTitle>Create New Event</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Event Name *</label>
                  <Input
                    type="text"
                    value={newEvent.name}
                    onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                    placeholder="e.g., Summer Music Festival"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Date *</label>
                    <Input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Start Time *</label>
                    <Input
                      type="time"
                      value={newEvent.time}
                      onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Location *</label>
                  <Input
                    type="text"
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="e.g., Central Park, New York"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Category *</label>
                  <select
                    value={newEvent.category}
                    onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Description</label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="4"
                    placeholder="Tell people about your event..."
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Cover Image (optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  {coverImagePreview && (
                    <div className="mt-2">
                      <img src={coverImagePreview} alt="Cover preview" className="w-full h-48 object-cover rounded-lg" />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Capacity (optional)</label>
                    <Input
                      type="number"
                      value={newEvent.capacity}
                      onChange={(e) => setNewEvent({ ...newEvent, capacity: e.target.value })}
                      placeholder="Max attendees"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Ticket Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newEvent.ticketPrice}
                      onChange={(e) => setNewEvent({ ...newEvent, ticketPrice: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newEvent.isPublic}
                      onChange={(e) => setNewEvent({ ...newEvent, isPublic: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">Public Event</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newEvent.requiresRegistration}
                      onChange={(e) => setNewEvent({ ...newEvent, requiresRegistration: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700">Requires Registration</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={createEvent}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    Create Event
                  </Button>
                  <Button
                    onClick={() => setActiveTab('browse')}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
